import React from 'react';
import PropTypes from 'prop-types';
import round from '../../../helpers/round';
import graphImage from '../../../assets/calculations/usNavy1982/usNavy1982_modified.JPG';

const GRAPH_WIDTH = 600;
const IMAGE_SIZE = { width: 1201, height: 835 };

const AXIS = {
  xLeft: 159,
  xRight: 1072,
  yTop: 34,
  yBottom: 708,
};

const LOG_LIMITS = {
  min: 1e-4,
  max: 1e-2,
};

// Calibrated from user-picked points on the left axis (cm^2/s):
// 1e-2 -> imgY 137.9, 1e-3 -> 374.2, 1e-4 -> 610.5
const AXIS_LOG_Y = {
  top: 137.9,
  mid: 374.2,
  bottom: 610.5,
};

// Calibrated vertical extent for LL line (imgY)
const LL_LINE_Y = {
  top: 31.7,
  bottom: 704.3,
};

const CM2S_TO_M2YR = 1 / 0.000317098;

const CURVE_POINTS = {
  cv1: [
    { ll: 32, y: 30.1 },
    { ll: 34, y: 42.3 },
    { ll: 36, y: 58.4 },
    { ll: 37, y: 66.8 },
    { ll: 38, y: 76.6 },
    { ll: 40, y: 92.8 },
    { ll: 42, y: 111.0 },
    { ll: 44, y: 125.2 },
    { ll: 50, y: 170.5 },
    { ll: 60, y: 248.6 },
    { ll: 70, y: 314.6 },
    { ll: 80, y: 378.7 },
    { ll: 90, y: 426.7 },
    { ll: 100, y: 468.8 },
    { ll: 110, y: 502.8 },
    { ll: 120, y: 532.8 },
  ],
  cv2: [
    { ll: 30, y: 205.2 },
    { ll: 40, y: 265.3 },
    { ll: 50, y: 321.3 },
    { ll: 60, y: 373.4 },
    { ll: 70, y: 421.4 },
    { ll: 80, y: 461.4 },
    { ll: 90, y: 503.5 },
    { ll: 100, y: 537.5 },
    { ll: 110, y: 569.5 },
    { ll: 120, y: 597.5 },
    { ll: 130, y: 623.6 },
    { ll: 140, y: 645.6 },
    { ll: 150, y: 661.6 },
    { ll: 160, y: 677.6 },
  ],
  cv3: [
    { ll: 30, y: 354.0 },
    { ll: 40, y: 408.1 },
    { ll: 50, y: 460.1 },
    { ll: 60, y: 498.1 },
    { ll: 70, y: 534.2 },
    { ll: 80, y: 560.2 },
    { ll: 90, y: 586.2 },
    { ll: 100, y: 608.2 },
    { ll: 110, y: 626.2 },
    { ll: 120, y: 642.3 },
  ],
};

const scaleForWidth = (width) => {
  const height = (width * IMAGE_SIZE.height) / IMAGE_SIZE.width;
  return {
    width,
    height,
    scaleX: width / IMAGE_SIZE.width,
    scaleY: height / IMAGE_SIZE.height,
  };
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// Calibrated from user-picked points on the chart:
// LL 40 -> imgX 279.3, LL 60 -> 400.7, LL 80 -> 522.1, LL 100 -> 645.7, LL 120 -> 767.1, LL 140 -> 888.5
const LL_X_SLOPE = 6.092;
const LL_X_INTERCEPT = 35.62;

const mapX = (ll, dims) => {
  const imgX = LL_X_SLOPE * ll + LL_X_INTERCEPT;
  const clampedImgX = clamp(imgX, AXIS.xLeft, AXIS.xRight);
  return clampedImgX * dims.scaleX + 1;
};

const mapY = (cvCm2PerSec, dims) => {
  if (!cvCm2PerSec || cvCm2PerSec <= 0) return null;
  const logMin = Math.log10(LOG_LIMITS.min);
  const logMax = Math.log10(LOG_LIMITS.max);
  const logVal = Math.log10(cvCm2PerSec);
  const t = (logVal - logMin) / (logMax - logMin);
  const imgY = AXIS_LOG_Y.bottom - clamp(t, 0, 1) * (AXIS_LOG_Y.bottom - AXIS_LOG_Y.top);
  return imgY * dims.scaleY;
};

const imgYToCv = (imgY) => {
  const logMin = Math.log10(LOG_LIMITS.min);
  const logMax = Math.log10(LOG_LIMITS.max);
  const t = (AXIS_LOG_Y.bottom - imgY) / (AXIS_LOG_Y.bottom - AXIS_LOG_Y.top);
  const logVal = logMin + t * (logMax - logMin);
  return Math.pow(10, logVal);
};

const interpolateCurveY = (points, ll) => {
  if (!points || points.length === 0 || !Number.isFinite(ll)) return null;
  const sorted = [...points].sort((a, b) => a.ll - b.ll);
  if (ll < sorted[0].ll || ll > sorted[sorted.length - 1].ll) return null;
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const p1 = sorted[i];
    const p2 = sorted[i + 1];
    if (ll >= p1.ll && ll <= p2.ll) {
      const t = (ll - p1.ll) / (p2.ll - p1.ll);
      return p1.y + t * (p2.y - p1.y);
    }
  }
  return null;
};

const formatValue = (value, decimals) => {
  if (!Number.isFinite(value)) return '';
  return round(value, decimals);
};

const formatScientific = (value, decimals = 2) => {
  if (!Number.isFinite(value) || value === 0) return '0';
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const mantissa = value / Math.pow(10, exponent);
  return `${mantissa.toFixed(decimals)}x10${exponent}`;
};

const CvLLUsNavyGraphSvg = ({ ll, cvValues, showOverlay, width = GRAPH_WIDTH, stroke = '#b33a3a' }) => {
  const llValue = Number(ll);
  const dims = scaleForWidth(width);
  const xLeft = AXIS.xLeft * dims.scaleX;
  const xRight = AXIS.xRight * dims.scaleX;
  const llLineTop = LL_LINE_Y.top * dims.scaleY + 1;
  const llLineBottom = LL_LINE_Y.bottom * dims.scaleY + 1;
  const x = mapX(llValue, dims);

  const overlays = cvValues
    .map((item) => {
      const curvePoints = CURVE_POINTS[item.key];
      const imgY = interpolateCurveY(curvePoints, llValue);
      if (imgY !== null) {
        const cvFromCurve = imgYToCv(imgY);
        return {
          ...item,
          y: imgY * dims.scaleY,
          cvCm2PerSecond: cvFromCurve,
          cvM2PerYear: cvFromCurve * CM2S_TO_M2YR,
        };
      }
      return {
        ...item,
        y: mapY(item.cvCm2PerSecond, dims),
      };
    })
    .filter((item) => item.show && item.y !== null);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${dims.width} ${dims.height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="US Navy 1982 LL vs Cv correlation graph"
      style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: `${dims.width} / ${dims.height}` }}
    >
      <image href={graphImage} x="0" y="0" width={dims.width} height={dims.height} />

      {showOverlay && overlays.length > 0 && (
        <>
          <g stroke={stroke} fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke">
            <line x1={x} y1={llLineTop} x2={x} y2={llLineBottom} stroke="#2b6cb0" />
            {overlays.map((item) => (
              <g key={item.key}>
                <line x1={xLeft} y1={item.y} x2={xRight} y2={item.y} />
                <polyline points={`${xLeft + 6},${item.y - 3} ${xLeft + 1},${item.y} ${xLeft + 6},${item.y + 3}`} />
                <polyline points={`${xRight - 6},${item.y - 3} ${xRight - 1},${item.y} ${xRight - 6},${item.y + 3}`} />
                <circle cx={x} cy={item.y} r={3} fill={stroke} stroke="none" />
              </g>
            ))}
          </g>
          <text x={x} y={llLineTop - 4} fill="#2b6cb0" fontSize="11" fontWeight="400" textAnchor="middle" stroke="none">
            LL={round(llValue, 1)}%
          </text>
        </>
      )}

      {showOverlay &&
        overlays.map((item) => (
          <g key={`${item.key}-labels`} fill={stroke} fontSize="9" fontWeight="700">
            <text x={xLeft - 4} y={item.y - 2} textAnchor="end">
              {formatScientific(item.cvCm2PerSecond, 2)}
            </text>
            <text x={xRight + 4} y={item.y - 2} textAnchor="start">
              {formatValue(item.cvM2PerYear, 2)}
            </text>
          </g>
        ))}
    </svg>
  );
};

CvLLUsNavyGraphSvg.propTypes = {
  ll: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  cvValues: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      show: PropTypes.bool.isRequired,
      cvM2PerYear: PropTypes.number,
      cvCm2PerSecond: PropTypes.number,
    }),
  ).isRequired,
  showOverlay: PropTypes.bool,
  width: PropTypes.number,
  stroke: PropTypes.string,
};

CvLLUsNavyGraphSvg.defaultProps = {
  showOverlay: false,
  width: GRAPH_WIDTH,
  stroke: '#b33a3a',
};

export default React.memo(CvLLUsNavyGraphSvg);
